import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Zap, Gauge, Wind, Droplets, Flame, Layers, Sparkles, FlaskConical, Leaf, Fuel, Link as LinkIcon, Search, X } from 'lucide-react';
import { products, productImages } from '../../data/mock';
import { useInView } from '../../hooks/useInView';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';

const iconMap = {
  Zap, Gauge, Wind, Droplets, Flame, Layers, Sparkles, FlaskConical, Leaf, Fuel, Link: LinkIcon,
  Pickaxe: Sparkles // fallback
};

export const ProductCategories = () => {
  const [ref, isInView] = useInView();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter products based on search query
  const filteredProducts = products.filter((product) => {
    const query = searchQuery.toLowerCase();
    return (
      product.name.toLowerCase().includes(query) ||
      product.brand.toLowerCase().includes(query) ||
      product.shortDescription.toLowerCase().includes(query) ||
      product.standard.toLowerCase().includes(query)
    );
  });

  return (
    <section id="products" className="py-20 lg:py-28 bg-[hsl(210,20%,98%)]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-10 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="section-divider" />
            <span className="text-sm font-semibold text-ind-red-600 uppercase tracking-wider">Products</span>
            <div className="section-divider" />
          </div>
          <h2 className="font-heading font-extrabold text-navy-950 text-3xl lg:text-4xl mb-3">
            Industrial Hoses & Safety Products
          </h2>
          <p className="text-navy-500 text-base max-w-2xl mx-auto">
            Comprehensive range of ISI certified industrial hoses and electrical safety solutions for every application.
          </p>
        </div>

        {/* Search Bar */}
        <div className={`max-w-md mx-auto mb-10 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
            <Input
              type="text"
              placeholder="Search products... (e.g., hydraulic, insulating mat, steam)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-10 h-12 rounded-full border-navy-200 bg-white shadow-sm focus:border-ind-red-500 focus:ring-ind-red-500 text-sm"
              data-testid="product-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
                data-testid="clear-search-btn"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-center text-sm text-navy-500 mt-3">
              Found <span className="font-semibold text-ind-red-600">{filteredProducts.length}</span> product{filteredProducts.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </p>
          )}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredProducts.map((product, idx) => {
            const IconComponent = iconMap[product.icon] || Zap;
            return (
              <Link
                to={`/products/${product.slug}`}
                key={product.slug}
                className={`card-hover group bg-white rounded-xl border border-navy-100 overflow-hidden ${
                  isInView ? 'animate-fade-in-up' : 'opacity-0'
                }`}
                style={{ animationDelay: `${Math.min(idx * 0.05, 0.5)}s` }}
                data-testid={`product-card-${product.slug}`}
              >
                {/* Product Image if available */}
                {productImages[product.slug] && (
                  <div className="h-36 bg-navy-50 overflow-hidden">
                    <img
                      src={productImages[product.slug]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-5">
                  {/* Icon + Badge */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-11 h-11 rounded-lg bg-navy-950 flex items-center justify-center text-ind-red-400 group-hover:bg-ind-red-600 group-hover:text-white transition-colors duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    {product.certified && (
                      <Badge variant="outline" className="text-[10px] border-green-200 text-green-700 bg-green-50 font-semibold">
                        <ShieldCheck className="w-3 h-3 mr-1" />
                        ISI Certified
                      </Badge>
                    )}
                  </div>

                  {/* Content */}
                  <h3 className="font-heading font-bold text-navy-900 text-sm mb-1.5 group-hover:text-ind-red-600 transition-colors duration-200">
                    {product.name}
                  </h3>
                  <p className="text-xs text-navy-500 mb-1 font-medium">{product.brand}</p>
                  <p className="text-xs text-navy-400 mb-3">{product.standard}</p>

                  <p className="text-xs text-navy-600 leading-relaxed mb-4 line-clamp-2">
                    {product.shortDescription}
                  </p>

                  {/* CTA */}
                  <div className="flex items-center gap-1 text-xs font-semibold text-ind-red-600 group-hover:gap-2">
                    View Details & Get Quote
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* No Results */}
        {filteredProducts.length === 0 && searchQuery && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-navy-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-navy-700 text-lg mb-2">No products found</h3>
            <p className="text-navy-500 text-sm mb-4">Try searching with different keywords like "hydraulic", "insulating mat", or "steam"</p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-ind-red-600 font-semibold text-sm hover:underline"
            >
              Clear search
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
